import moment from "moment";
import { useMemo } from "react";
import { OnlineVideoConference } from "types/graphql-schema";

import MeetingVideoConferenceButton from "@apps/meeting-new/components/meeting/video-conference-button";
import Link from "@components/link/link";
import Tooltip from "@components/tooltip/tooltip";
import { classNames } from "@helpers/css";
import { getUrl } from "@helpers/helpers";

const SidebarMeeting = ({
  meetingId,
  meetingGroupId,
  title,
  draft,
  videoConferenceUrl,
  videoConferenceType,
  startDatetime,
  endDatetime,
  now,
}: {
  title?: string | null;
  meetingId: number;
  meetingGroupId?: number;
  startDatetime?: string | null;
  endDatetime?: string | null;
  draft?: boolean;
  videoConferenceType?: OnlineVideoConference | null;
  videoConferenceUrl?: string | null;
  now?: number;
}) => {
  const meetingPath = useMemo(
    () => getUrl({ meetingGroupId, meetingId }),
    [meetingGroupId, meetingId],
  );
  const isActive = window.location.href.includes(meetingPath);
  const tsStartDatetime = useMemo(
    () => (startDatetime ? new Date(startDatetime).getTime() : null),
    [startDatetime],
  );
  const tsEndDatetime = useMemo(
    () => (endDatetime ? new Date(endDatetime).getTime() : null),
    [endDatetime],
  );
  const isInProgress = useMemo(
    () =>
      now && tsStartDatetime && tsEndDatetime
        ? now > tsStartDatetime && now < tsEndDatetime
        : false,
    [tsStartDatetime, tsEndDatetime, now],
  );
  const isAboutToStartProgress = useMemo(
    () =>
      now && tsStartDatetime && tsEndDatetime
        ? now > tsStartDatetime - 6 * 60 * 1000 && now < tsEndDatetime
        : false,
    [tsStartDatetime, tsEndDatetime, now],
  );
  const isPast = useMemo(
    () => (now && !!tsEndDatetime ? tsEndDatetime < now : false),
    [tsEndDatetime, now],
  );
  const timeLabel = useMemo(
    () => moment(startDatetime).format("h:mma"),
    [startDatetime],
  );

  if (!meetingGroupId) {
    return null;
  }

  return (
    <Link
      key={meetingId}
      to={meetingPath}
      className={classNames(
        "block rounded-md py-2 pl-2.5 pr-2 border bg-white border-gray-200 hover:border-gray-300",
        isActive
          ? "border-blue-400 hover:border-blue-500"
          : draft
            ? "hover:border-amber-500 border-dashed border-amber-400 bg-amber-50"
            : isInProgress
              ? "border-emerald-400 hover:border-emerald-500"
              : isPast && "opacity-40 bg-gray-100",
      )}
    >
      <div className="flex items-start gap-1.5">
        <Tooltip text={title} delay={1000}>
          <div
            className={classNames(
              "flex flex-1 text-xs text-gray-800 font-medium",
              "min-w-0", // to break words that are too long
            )}
          >
            <span className="truncate tracking-[-.015em]">{title}</span>
          </div>
        </Tooltip>
        {startDatetime && (
          <div
            className={classNames(
              "text-xs rounded-full",
              isActive
                ? "text-blue-600"
                : draft
                  ? "text-amber-800"
                  : isInProgress
                    ? "text-emerald-600"
                    : "text-gray-500",
            )}
          >
            {timeLabel}
          </div>
        )}
      </div>
      {videoConferenceUrl && isAboutToStartProgress && (
        <div className="mt-1 text-sm text-gray-700 -ml-1 flex">
          <div>
            <MeetingVideoConferenceButton
              meeting={{
                startDatetime,
                videoConferenceType,
                videoConferenceUrl,
              }}
              className="hover:text-gray-800 hover:bg-black/5 text-xs px-1 h-[22px] rounded text-gray-600 gap-1.5 tracking-tight"
            />
          </div>
        </div>
      )}
    </Link>
  );
};

export default SidebarMeeting;
